import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Brain, Sparkle, ArrowRight, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { isCoachRole } from "@/lib/roles";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { Disclaimer } from "@/components/Disclaimer";

const AI_BG = "https://static.prod-images.emergentagent.com/jobs/c0bc3daa-f83a-41ae-9198-63d10955a29e/images/5fc1e979542cd538c45d21deef42d5c1c185fc06df4a528c99680a0aaf757731.png";

const SECTION_META = {
  warmup:       { label: "Warm-up",     color: "#F97316" },
  activation:   { label: "Activation",  color: "#EAB308" },
  speed:        { label: "Speed",       color: "#06B6D4" },
  power:        { label: "Power",       color: "#A855F7" },
  strength:     { label: "Strength",    color: "#EF4444" },
  accessory:    { label: "Accessory",   color: "#3B82F6" },
  conditioning: { label: "Conditioning",color: "#22C55E" },
  recovery:     { label: "Recovery",    color: "#14B8A6" },
};
const SECTION_ORDER = ["warmup", "activation", "speed", "power", "strength", "accessory", "conditioning", "recovery"];

export default function AiCoach() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCoach = isCoachRole(user?.primary_role);
  const [form, setForm] = useState({
    goal: "Build muscle and gain strength",
    experience: "Intermediate",
    days_per_week: 4,
    equipment: "Full Gym",
    focus: "Hypertrophy",
    weeks: 4,
    scheme: "automatic",
    base_1rms_text: "Back Squat: 100\nBench Press: 80\nDeadlift: 140",
  });
  const [loading, setLoading] = useState(false);
  const [program, setProgram] = useState(null);
  const [history, setHistory] = useState([]);
  const [quota, setQuota] = useState(null);
  const [activeWeek, setActiveWeek] = useState(1);
  const [savingLibrary, setSavingLibrary] = useState(false);

  const saveToLibrary = async () => {
    if (!program?.program_id) return;
    setSavingLibrary(true);
    try {
      await api.post(`/ai/programs/${program.program_id}/save-to-library`);
      toast.success("Saved to your Program Builder library");
      navigate("/coach/programs");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingLibrary(false);
    }
  };

  const loadQuota = () => {
    api.get("/ai/program-quota").then(({ data }) => setQuota(data)).catch(() => {});
  };

  useEffect(() => {
    if (user && !isCoach) {
      // AI Program Builder is an Independent Coach tool per PRD.
      // Guard against direct-URL access by non-coach roles (nav already hides the link).
      navigate("/dashboard", { replace: true });
      return;
    }
    api.get("/ai/programs").then(({ data }) => {
      setHistory(data);
      if (data.length > 0) setProgram(data[0]);
    });
    loadQuota();
  }, [user, isCoach]);

  const canGenerate = quota?.unlimited || (quota?.remaining ?? 1) > 0;
  const showUpgrade = quota && !quota.unlimited && quota.remaining === 0;

  const generate = async () => {
    if (!form.goal.trim()) return toast.error("Tell me your goal");
    setLoading(true);
    try {
      // Parse "Exercise Name: 100" lines into a {name: kg} dict
      const base_1rms = {};
      (form.base_1rms_text || "").split(/\n+/).forEach((line) => {
        const m = line.match(/^\s*([A-Za-z][A-Za-z\s\-']+?)\s*[:=]\s*([0-9.]+)/);
        if (m) base_1rms[m[1].trim()] = Number(m[2]);
      });
      const payload = {
        goal: form.goal,
        experience: form.experience,
        days_per_week: Number(form.days_per_week),
        equipment: form.equipment,
        focus: form.focus,
        weeks: Number(form.weeks) || 4,
        scheme: form.scheme || "automatic",
        base_1rms,
      };
      const { data } = await api.post("/ai/program", payload);
      setProgram(data);
      setHistory([data, ...history]);
      toast.success(`Program ready. ${(data.program?.scheme_applied_to || []).length} lift(s) auto-progressed.`);
      loadQuota();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        toast.error(detail || "Free limit reached. Upgrade to continue.");
        loadQuota();
      } else {
        toast.error("AI generation failed. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const p = program?.program;

  return (
    <div className="relative" data-testid="ai-coach-page">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <img src={AI_BG} alt="" className="w-full h-96 object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-surface-base/40 via-surface-base/80 to-surface-base" />
      </div>

      <div className="relative p-6 lg:p-10 max-w-7xl mx-auto">
        <div className="mb-10">
          <div className="label-tiny text-brand-accent mb-2 flex items-center gap-2">
            <Sparkle size={12} weight="fill" /> POWERED BY CLAUDE SONNET 4.5
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-3">AI PROGRAM<br />DESIGNER.</h1>
          <p className="text-text-secondary max-w-xl">Describe your goal, equipment, and experience. The AI engineers a science-backed program in seconds.</p>
        </div>

        <Disclaimer variant="ai" className="mb-8 max-w-3xl" testid="aicoach-ai-disclaimer" />

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="tactical-card p-6 sticky top-6" data-testid="ai-form">
              <h3 className="font-heading text-lg mb-5 flex items-center gap-2">
                <Brain size={20} weight="duotone" className="text-brand-accent" />
                PARAMETERS
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="label-tiny block mb-2">GOAL</label>
                  <Input value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} className="bg-surface-elevated border-white/10" data-testid="ai-goal-input" />
                </div>
                <div>
                  <label className="label-tiny block mb-2">EXPERIENCE</label>
                  <Select value={form.experience} onValueChange={(v) => setForm({ ...form, experience: v })}>
                    <SelectTrigger className="bg-surface-elevated border-white/10" data-testid="ai-experience-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface-DEFAULT border-white/10 text-white">
                      {["Beginner", "Intermediate", "Advanced"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="label-tiny block mb-2">DAYS / WEEK</label>
                  <Select value={String(form.days_per_week)} onValueChange={(v) => setForm({ ...form, days_per_week: Number(v) })}>
                    <SelectTrigger className="bg-surface-elevated border-white/10" data-testid="ai-days-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface-DEFAULT border-white/10 text-white">
                      {[2,3,4,5,6].map((x) => <SelectItem key={x} value={String(x)}>{x} days</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="label-tiny block mb-2">EQUIPMENT</label>
                  <Select value={form.equipment} onValueChange={(v) => setForm({ ...form, equipment: v })}>
                    <SelectTrigger className="bg-surface-elevated border-white/10" data-testid="ai-equipment-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface-DEFAULT border-white/10 text-white">
                      {["Full Gym", "Home Gym", "Bodyweight"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="label-tiny block mb-2">FOCUS</label>
                  <Select value={form.focus} onValueChange={(v) => setForm({ ...form, focus: v })}>
                    <SelectTrigger className="bg-surface-elevated border-white/10" data-testid="ai-focus-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface-DEFAULT border-white/10 text-white">
                      {["Strength", "Hypertrophy", "Endurance", "Fat Loss", "Athleticism"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="label-tiny block mb-2">WEEKS</label>
                    <Select value={String(form.weeks)} onValueChange={(v) => setForm({ ...form, weeks: Number(v) })}>
                      <SelectTrigger className="bg-surface-elevated border-white/10" data-testid="ai-weeks-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-surface-DEFAULT border-white/10 text-white">
                        {[1,2,3,4,6,8,12].map((x) => <SelectItem key={x} value={String(x)}>{x} wk</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="label-tiny block mb-2">SCHEME</label>
                    <Select value={form.scheme} onValueChange={(v) => setForm({ ...form, scheme: v })}>
                      <SelectTrigger className="bg-surface-elevated border-white/10" data-testid="ai-scheme-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-surface-DEFAULT border-white/10 text-white">
                        {[
                          ["automatic", "Automatic"],
                          ["linear", "Linear"],
                          ["wave", "Wave"],
                          ["block", "Block"],
                          ["undulating", "Undulating"],
                          ["none", "No auto-progression"],
                        ].map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="label-tiny block mb-2">BASE 1RMS (LIFT: KG)</label>
                  <textarea
                    value={form.base_1rms_text}
                    onChange={(e) => setForm({ ...form, base_1rms_text: e.target.value })}
                    className="w-full bg-surface-elevated border border-white/10 p-2 text-xs font-mono min-h-[80px] text-white/90 placeholder:text-white/40"
                    placeholder="Back Squat: 140&#10;Bench Press: 110&#10;Deadlift: 180"
                    data-testid="ai-base-1rms-input"
                  />
                  <div className="text-[10px] text-text-secondary mt-1">
                    Each line: <span className="text-white/80">Exercise: kg</span>. Compound lifts you list get auto-progressed week-to-week via the scheme.
                  </div>
                </div>
                <button
                  onClick={generate}
                  disabled={loading || !canGenerate}
                  className="btn-kinetic w-full py-3 text-xs label-tiny tracking-widest disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="ai-generate-button"
                >
                  {loading
                    ? "ENGINEERING..."
                    : !canGenerate
                    ? <><Lock size={14} weight="bold" /> UPGRADE TO CONTINUE</>
                    : <>GENERATE PROGRAM <ArrowRight size={14} weight="bold" /></>}
                </button>

                {quota && !quota.unlimited && (
                  <div className="mt-3 text-[11px] label-tiny text-text-secondary text-center" data-testid="ai-quota-display">
                    {quota.remaining > 0
                      ? <>{quota.remaining} of {quota.limit} FREE {quota.limit === 1 ? "BUILD" : "BUILDS"} REMAINING</>
                      : <>YOU&apos;VE USED ALL {quota.limit} FREE {quota.limit === 1 ? "BUILD" : "BUILDS"}</>}
                  </div>
                )}

                {showUpgrade && (
                  <div className="mt-4 border border-heat/40 bg-heat/5 p-4" data-testid="ai-upgrade-notice">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkle size={16} weight="fill" className="text-heat" />
                      <span className="label-tiny text-heat">UPGRADE FOR UNLIMITED</span>
                    </div>
                    <p className="text-xs text-text-secondary mb-3">
                      You&apos;ve used your free {quota.limit === 1 ? "AI Program build" : `${quota.limit} AI Program builds`}.
                      Go Pro to generate unlimited programs plus advanced analytics.
                    </p>
                    <Link
                      to="/upgrade"
                      className="inline-flex items-center gap-1.5 bg-heat text-white px-3 py-2 text-xs label-tiny tracking-wider hover:brightness-110 transition"
                      data-testid="ai-upgrade-button"
                    >
                      GO PRO <ArrowRight size={12} weight="bold" />
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            {loading && (
              <div className="tactical-card p-12 text-center" data-testid="ai-loading">
                <Brain size={48} weight="duotone" className="text-brand-accent mx-auto mb-4 animate-pulse" />
                <h3 className="font-heading text-xl">DESIGNING YOUR PROGRAM</h3>
                <p className="text-sm text-text-secondary mt-2">Claude is analyzing your parameters...</p>
              </div>
            )}
            {!loading && !program && (
              <div className="tactical-card p-12 text-center" data-testid="ai-empty">
                <Sparkle size={36} weight="duotone" className="text-brand-accent mx-auto mb-4" />
                <h3 className="font-heading text-xl mb-2">READY WHEN YOU ARE</h3>
                <p className="text-sm text-text-secondary">Configure parameters and hit &ldquo;GENERATE PROGRAM&rdquo;.</p>
              </div>
            )}
            {!loading && p && (
              <div className="tactical-card p-8 animate-fade-up" data-testid="ai-program-result">
                <div className="mb-6 pb-4 border-b border-white/10 flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="label-tiny text-brand-accent mb-2">AI PROGRAM</div>
                    <h2 className="font-heading text-3xl mb-2">{p.name}</h2>
                    <p className="text-sm text-text-secondary">{p.summary}</p>
                    <div className="flex flex-wrap gap-4 mt-3 text-xs text-text-secondary">
                      <span><span className="label-tiny">WEEKS:</span> <span className="text-white tabular-nums">{p.weeks}</span></span>
                      <span><span className="label-tiny">DAYS/WK:</span> <span className="text-white tabular-nums">{new Set((p.days || []).filter((d) => d.week === 1).map((d) => d.day)).size || p.days?.length}</span></span>
                      {p.scheme && <span><span className="label-tiny">SCHEME:</span> <span className="text-yellow-400 tabular-nums">{p.scheme.toUpperCase()}</span></span>}
                      {p.scheme_applied_to?.length ? (
                        <span><span className="label-tiny">AUTO-PROGRESSED:</span> <span className="text-yellow-400">{p.scheme_applied_to.join(", ")}</span></span>
                      ) : null}
                    </div>
                  </div>
                  {isCoach && (
                    <button
                      onClick={saveToLibrary}
                      disabled={savingLibrary}
                      className="bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold px-4 py-2 text-xs label-tiny tracking-widest transition-colors disabled:opacity-60"
                      data-testid="ai-save-to-library"
                    >
                      {savingLibrary ? "SAVING..." : "SAVE TO MY PROGRAMS ↗"}
                    </button>
                  )}
                </div>

                {/* Week tabs (only if multi-week) */}
                {p.weeks > 1 && (
                  <div className="flex gap-1 mb-4 flex-wrap" data-testid="ai-week-tabs">
                    {Array.from({ length: p.weeks }, (_, i) => i + 1).map((w) => (
                      <button
                        key={w}
                        onClick={() => setActiveWeek(w)}
                        data-testid={`ai-week-tab-${w}`}
                        className={`px-3 py-1.5 text-xs label-tiny tracking-wider border transition ${activeWeek === w ? "bg-yellow-400 text-slate-900 border-yellow-400" : "border-white/15 text-text-secondary hover:text-white hover:border-white/40"}`}
                      >
                        WEEK {w}
                      </button>
                    ))}
                  </div>
                )}

                <div className="space-y-4">
                  {(p.days || []).filter((d) => (p.weeks > 1 ? d.week === activeWeek : true)).map((d, i) => (
                    <div key={`${d.week}-${d.day}-${i}`} className="border border-white/10 p-5" data-testid={`program-day-${d.week}-${d.day}`}>
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <h3 className="font-heading text-lg">W{d.week} · {d.name || d.day}</h3>
                        {d.session_type && <span className="label-tiny text-yellow-400">{String(d.session_type).toUpperCase()}</span>}
                      </div>
                      <AiSectionizedExercises exercises={d.exercises || []} />
                    </div>
                  ))}
                </div>

                {p.tips?.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-white/10">
                    <div className="label-tiny mb-3">COACH&rsquo;S NOTES</div>
                    <ul className="space-y-2">
                      {p.tips.map((t, i) => (
                        <li key={i} className="flex gap-2 text-sm">
                          <span className="text-brand-accent">▸</span>
                          <span className="text-text-secondary">{t}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {history.length > 1 && (
              <div className="mt-6">
                <div className="label-tiny mb-3">// PROGRAM HISTORY</div>
                <div className="flex flex-wrap gap-2">
                  {history.map((h) => (
                    <button
                      key={h.program_id}
                      onClick={() => setProgram(h)}
                      className={`px-3 py-2 text-xs label-tiny border transition-colors ${program?.program_id === h.program_id ? "border-brand-accent text-brand-accent" : "border-white/10 text-text-secondary hover:border-white/40"}`}
                      data-testid={`history-program-${h.program_id}`}
                    >
                      {h.program?.name || "Program"}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AiSectionizedExercises({ exercises }) {
  const groups = {};
  exercises.forEach((ex) => {
    const s = (ex.section || "strength").toLowerCase();
    (groups[s] ||= []).push(ex);
  });
  const orderedSections = SECTION_ORDER.filter((s) => groups[s]?.length);
  if (!orderedSections.length) return <div className="text-sm text-text-secondary italic">No exercises.</div>;
  return (
    <div className="space-y-3">
      {orderedSections.map((sid) => {
        const meta = SECTION_META[sid] || { label: sid, color: "#666" };
        return (
          <div key={sid} className="border border-white/5" data-testid={`ai-section-${sid}`}>
            <div className="px-3 py-1.5 text-[11px] label-tiny tracking-wider" style={{ color: meta.color, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              // {meta.label.toUpperCase()} · {groups[sid].length}
            </div>
            <table className="w-full text-sm">
              <tbody>
                {groups[sid].map((ex, j) => (
                  <tr key={j} className="border-t border-white/[0.03]">
                    <td className="py-2 px-3 align-top">
                      <div className="font-medium">{ex.name}</div>
                      {ex.coach_cue && <div className="text-[11px] text-text-secondary italic mt-0.5">{ex.coach_cue}</div>}
                      {(ex.tempo || ex.tags) && (
                        <div className="text-[10px] text-text-secondary/70 mt-0.5 tabular-nums">
                          {ex.tempo ? `TEMPO ${ex.tempo}` : null}
                          {ex.tempo && ex.tags ? " · " : null}
                          {ex.tags || ""}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums w-14">
                      <div>{ex.sets}<span className="text-text-secondary/60">×</span>{ex.reps}</div>
                      {ex.rest_sec ? <div className="text-[10px] text-text-secondary/60">{ex.rest_sec}s</div> : null}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums w-24">
                      {ex.weight_kg ? (
                        <>
                          <div className="text-yellow-400 font-bold">{ex.weight_kg}<span className="text-[10px] text-text-secondary/60 ml-0.5">kg</span></div>
                          {ex.percentage_1rm ? <div className="text-[10px] text-yellow-400/80">{ex.percentage_1rm}% 1RM</div> : null}
                        </>
                      ) : <span className="text-text-secondary/40 text-[11px]">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
